import React, { useEffect, useState, useCallback } from 'react';
import FormControl from '../common/FormControl';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Pagination from '../common/Pagination';
import Toast, { ToastMessage } from '../common/Toast';
import { confirmWarning, confirmActive } from '../common/confirm';
import extractApiError from '../../utils/apiError';
import NoDataFound from '../common/NoDataFound';
import Skeleton from '../common/Skeleton';
import { AlertCircle, ShieldAlert, Activity, Radio, Plus, Check, Search, CircleMinus, CircleCheck, Edit2, Lock } from 'lucide-react';
import {
    processesApi, risksApi, categoriesApi, incidentTypesApi, fetchCategoriesDropdown,
    IRGProcess, IRGRisk, IRGCategory, IRGIncidentType,
} from '../../services/incidentService';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea } from '../../validation/ValidatedFields';
import { isUniqueLocal } from '../../validation/uniqueness';
import { usePermission, NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';
import ApprovalDot, { WorkflowState } from '../common/ApprovalDot';
import WorkflowModal from '../common/WorkflowModal';
import WorkflowSaveBar, { SaveTarget } from '../common/WorkflowSaveBar';
import StatusSwitch from '../common/StatusSwitch';

const UNIQ_CODE: Record<MasterKey, number> = {
    incidents: 928,
    risks: 929,
    processes: 930,
    categories: 944,
};

// Maker-checker WF kind per master (registered in backend WF_RESOURCES, all gated
// by IRG_MASTERS). The row ApprovalDot opens the shared WorkflowModal by kind+id.
const WF_KIND: Record<MasterKey, string> = {
    incidents: 'irg_incident_type',
    risks: 'irg_risk_type',
    processes: 'irg_process_type',
    categories: 'irg_event_category',
};

export type MasterKey = 'incidents' | 'risks' | 'processes' | 'categories';

interface NormalizedItem {
    id: number;
    name: string;
    description: string;
    status: 'Active' | 'Inactive';
    workflow_state: WorkflowState;
    incident_category_id?: number;
    code?: string | null;
    raw: any;
}

function normalize(key: MasterKey, raw: any): NormalizedItem {
    const wf = (raw.workflow_state || 'draft') as WorkflowState;
    if (key === 'incidents') {
        const r = raw as IRGIncidentType;
        return { id: r.id, name: r.incident_name, description: r.description || '',
            status: r.is_active === 1 ? 'Active' : 'Inactive', workflow_state: wf, incident_category_id: r.incident_category_id, code: (raw as any).code ?? null, raw };
    }
    if (key === 'risks') {
        const r = raw as IRGRisk;
        return { id: r.id, name: r.risk_name, description: r.risk_description || '',
            status: r.is_active === 1 ? 'Active' : 'Inactive', workflow_state: wf, code: (raw as any).code ?? null, raw };
    }
    if (key === 'processes') {
        const r = raw as IRGProcess;
        return { id: r.id, name: r.process_name, description: r.process_description || '',
            status: r.is_active === 1 ? 'Active' : 'Inactive', workflow_state: wf, code: (raw as any).code ?? null, raw };
    }
    const r = raw as IRGCategory;
    return { id: r.id, name: r.category_name, description: r.description || '',
        status: r.is_active === 1 ? 'Active' : 'Inactive', workflow_state: wf, code: (raw as any).code ?? null, raw };
}

function buildPayload(key: MasterKey, form: { name: string; description: string; incident_category_id?: number }, isCreate: boolean) {
    const base: any = isCreate ? { is_active: 1 } : {};
    if (key === 'incidents') return { ...base, incident_name: form.name, description: form.description, incident_category_id: form.incident_category_id || 0 };
    if (key === 'risks') return { ...base, risk_name: form.name, risk_description: form.description };
    if (key === 'processes') return { ...base, process_name: form.name, process_description: form.description };
    return { ...base, category_name: form.name, description: form.description };
}

function apiFor(key: MasterKey) {
    if (key === 'incidents') return incidentTypesApi;
    if (key === 'risks') return risksApi;
    if (key === 'processes') return processesApi;
    return categoriesApi;
}

export const MASTERS: { key: MasterKey; label: string; icon: React.ElementType }[] = [
    { key: 'incidents',  label: 'Type of Incidents', icon: AlertCircle },
    { key: 'risks',      label: 'Type of Risks',     icon: ShieldAlert },
    { key: 'processes',  label: 'Type of Process',   icon: Activity    },
    { key: 'categories', label: 'Category (Event)',  icon: Radio       },
];

// --- CRUD Panel (renders inside the right card) ---
interface MasterCRUDProps {
    title: string;
    masterKey: MasterKey;
}

export const MasterCRUD: React.FC<MasterCRUDProps> = ({ title, masterKey }) => {
    // All four IR masters now surface an auto-generated backend `code` column.
    const showCode = true;
    const [items, setItems] = useState<NormalizedItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState<NormalizedItem | null>(null);
    const [formData, setFormData] = useState<{ name: string; description: string; incident_category_id?: number }>(
        { name: '', description: '' }
    );
    const [categoryOptions, setCategoryOptions] = useState<{ id: number; name: string }[]>([]);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [toggling, setToggling] = useState(false);
    // View-first edit: existing records open read-only; the user clicks "Edit"
    // to enable the fields. Create always opens editable.
    const [isEditing, setIsEditing] = useState(false);
    const [workflowRow, setWorkflowRow] = useState<NormalizedItem | null>(null);
    const fieldVh = useFieldValidation();
    const { allowed: canView, loading: viewLoading, message: viewMsg } = usePermission('IRG_MASTERS', 'view');
    const { allowed: canModify, loading: permLoading } = usePermission('IRG_MASTERS', 'view+modify');
    const { allowed: canApproveExtent } = usePermission('IRG_MASTERS', 'view+modify+approve');

    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<'Active' | 'Inactive'>('Active');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const reload = useCallback(async () => {
        if (!canView) return;
        setLoading(true);
        try {
            const data = await apiFor(masterKey).list({ status: 'ALL' });
            setItems(data.map((d: any) => normalize(masterKey, d)));
        } catch (e: any) {
            setToast({ type: 'error', message: e?.response?.data?.detail || e?.message || 'Failed to load' });
        } finally {
            setLoading(false);
        }
    }, [masterKey, canView]);

    useEffect(() => { reload(); }, [reload]);

    const loadCategories = useCallback(() => {
        if (masterKey === 'incidents') {
            fetchCategoriesDropdown().then(r => setCategoryOptions(r.items)).catch(() => setCategoryOptions([]));
        }
    }, [masterKey]);

    useEffect(() => { loadCategories(); }, [loadCategories]);

    useEffect(() => { setPage(1); }, [searchTerm, statusFilter, pageSize]);

    const filteredItems = items
        .filter(item => {
            const matchesSearch = !searchTerm ||
                item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.description.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesStatus = item.status === statusFilter;
            return matchesSearch && matchesStatus;
        })
        .sort((a, b) => b.id - a.id);

    const totalRecords = filteredItems.length;
    const paginatedItems = filteredItems.slice((page - 1) * pageSize, page * pageSize);

    const handleAdd = () => {
        setCurrentItem(null);
        setFormData({ name: '', description: '', incident_category_id: undefined });
        fieldVh.reset();
        setSubmitting(false);
        setToggling(false);
        setIsEditing(true); // create is always editable
        loadCategories();
        setIsModalOpen(true);
    };

    // Open an existing record in read-only (view) mode. The "Edit" button in
    // the footer switches to editable. Viewing is allowed for anyone with the
    // `view` extent; modifying still requires `view+modify`.
    const handleEdit = (item: NormalizedItem) => {
        setCurrentItem(item);
        setFormData({ name: item.name, description: item.description, incident_category_id: item.incident_category_id });
        fieldVh.reset();
        setSubmitting(false);
        setToggling(false);
        setIsEditing(false);
        loadCategories();
        setIsModalOpen(true);
    };

    // Approved state — determines if form is locked
    const isApproved = (currentItem as any)?.workflow_state === 'approved';
    // Fields are read-only when viewing an existing record (before "Edit" is
    // clicked) or when the user lacks modify permission.
    const fieldsDisabled = !!currentItem && (!isEditing || !canModify || isApproved);

    const handleToggleActive = async () => {
        if (!currentItem) return;
        const next = currentItem.status === 'Active' ? 0 : 1;
        const isActive = currentItem.status === 'Active';
        const confirmFn = isActive ? confirmWarning : confirmActive;
        const ok = await confirmFn({
            title: next ? `Reactivate this ${title.toLowerCase()}?` : `Inactivate this ${title.toLowerCase()}?`,
            text: next
                ? 'It will appear in active lists again.'
                : 'It will be hidden from active lists.',
            confirmText: next ? 'Yes, Reactivate' : 'Yes, Inactivate',
        });
        if (!ok) return;
        setToggling(true);
        try {
            await apiFor(masterKey).update(currentItem.id, { is_active: next } as any);
            setIsModalOpen(false);
            await reload();
            setToast({ type: next ? 'success' : 'warning', message: next ? 'Reactivated successfully.' : 'Inactivated successfully.' });
        } catch (err: any) {
            setToast({ type: 'error', message: extractApiError(err, 'Failed to update status') });
        } finally {
            setToggling(false);
        }
    };

    const handleSave = async (action: SaveTarget = 'draft', e?: React.FormEvent) => {
        e?.preventDefault();
        if (!canModify) { setToast({ type: 'error', message: NOT_AUTHORISED_MSG }); return; }
        // Format-level validation (engine 109 name / 110 description).
        const formatOk = fieldVh.validateAll([
            { name: 'name', code: 109, value: formData.name || '', required: true },
            { name: 'description', code: 110, value: formData.description || '' },
        ]);
        // Category gate — unchanged behaviour (incidents only, required).
        let gateOk = true;
        if (masterKey === 'incidents' && !formData.incident_category_id) {
            fieldVh.setError('incident_category_id', 'Please select a category');
            gateOk = false;
        }
        if (!formatOk || !gateOk) return;
        // Uniqueness (entity-scoped) — case-insensitive local pre-check.
        if (!isUniqueLocal(UNIQ_CODE[masterKey], formData.name || '', items, currentItem?.id ?? undefined)) {
            fieldVh.setError('name', `${title} with this name already exists`);
            return;
        }
        setSubmitting(true);
        try {
            const api = apiFor(masterKey);
            const payload = buildPayload(masterKey, formData, !currentItem);
            if (currentItem) {
                await api.update(currentItem.id, payload as any);
                setToast({ type: 'success', message: `${title} updated successfully.` });
            } else {
                await api.create(payload as any, action);
                setToast({ type: 'success', message: ({
                    draft: `${title} saved as draft.`,
                    submit: `${title} submitted for approval.`,
                    approve: `${title} created & approved.`,
                } as Record<SaveTarget, string>)[action] });
            }
            setIsModalOpen(false);
            await reload();
        } catch (err: any) {
            const msg = extractApiError(err, 'Save failed');
            if (/exist|duplicate|unique/i.test(msg)) {
                fieldVh.setError('name', msg);
            } else {
                setToast({ type: 'error', message: msg });
            }
        } finally {
            setSubmitting(false);
        }
    };

    // Not-authorised gate — mirrors RiskCategoriesTab / ControlCategoriesTab so
    // every Masters leaf behaves consistently when the user lacks `view`.
    if (!viewLoading && !canView) {
        return (
            <div className="netbody-card flex flex-col h-full">
                <NoDataFound title="Not authorised" message={viewMsg || NOT_AUTHORISED_MSG} />
            </div>
        );
    }

    return (
        <>
            {/* Toolbar — Status switch LEFT · search + Create RIGHT */}
            <div className="netbody-header">
                <StatusSwitch
                    value={statusFilter === 'Inactive' ? 'INACTIVE' : 'ACTIVE'}
                    onChange={next => setStatusFilter(next === 'INACTIVE' ? 'Inactive' : 'Active')}
                    noun="records"
                    testId="BTN_incident_master_status"
                />
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.Input
                        data-testid="INP_incident_master_search"
                        type="text"
                        placeholder={`Search ${title.toLowerCase()}…`}
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                    />
                    <Button
                        data-testid="BTN_incident_master_create"
                        variant="fill"
                        icon={Plus}
                        onClick={handleAdd}
                        disabled={permLoading || !canModify}
                        title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                    >
                        Create
                    </Button>
                </div>
            </div>

            {/* Body */}
            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                    <div className="table-list">
                        <div className="table-list-scroll">
                            <table className="table-list-table">
                                <thead>
                                    <tr>
                                        <th className="table-list-th table-list-cell--dot" />
                                        <th className="table-list-th table-list-cell--sno">S.No</th>
                                        {showCode && <th className="table-list-th">Code</th>}
                                        <th className="table-list-th">Name</th>
                                        <th className="table-list-th">Description</th>
                                    </tr>
                                </thead>
                                <tbody aria-busy={loading} aria-live="polite">
                                    {loading && <Skeleton.TableRows cols={showCode ? ['3xs', '2xs', '2xs', 'md', 'xs'] : ['3xs', '2xs', 'md', 'xs']} />}
                                    {!loading && filteredItems.length === 0 && (
                                        <tr>
                                            <td colSpan={showCode ? 5 : 4} className="table-list-cell">
                                                <NoDataFound
                                                    title={searchTerm || statusFilter !== 'Active' ? 'No Matching Records' : `No ${title} Found`}
                                                    message={searchTerm || statusFilter !== 'Active' ? 'No records match your filters.' : 'Click "Create" to add one.'}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                    {!loading && paginatedItems.map((item, index) => (
                                            <tr
                                                data-testid={`BTN_incident_master_row_${item.id}_open`}
                                                key={item.id}
                                                className={`table-list-row${item.status === 'Active' ? '' : ' is-inactive'}`}
                                                onClick={() => handleEdit(item)}
                                                title="View details"
                                            >
                                                <td className="table-list-cell table-list-cell--dot" onClick={(e) => { e.stopPropagation(); setWorkflowRow(item); }} title="Workflow — view timeline & act">
                                                    <ApprovalDot
                                                        state={item.workflow_state}
                                                        onClick={() => setWorkflowRow(item)}
                                                    />
                                                </td>
                                                <td className="table-list-cell table-list-cell--sno">
                                                    {(page - 1) * pageSize + index + 1}
                                                </td>
                                                {showCode && (
                                                    <td className="table-list-cell" data-testid={`TXT_incident_${masterKey}_row_${item.id}_code`}>
                                                        {item.code ? <span className="cell-code">{item.code}</span> : <span className="text-muted">—</span>}
                                                    </td>
                                                )}
                                                <td className="table-list-cell">
                                                    <span className="table-list-cell-title cell-name-lock">
                                                        {(item as any).workflow_state === 'approved' && (
                                                            <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                                                        )}
                                                        {item.name}
                                                    </span>
                                                </td>
                                                <td className="table-list-cell table-list-cell--truncate" title={item.description}>
                                                    {item.description || '—'}
                                                </td>
                                            </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalRecords={totalRecords}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            pageSizeOptions={[10, 25, 50, 100]}
                            recordLabel={title.toLowerCase()}
                        />
                    </div>
                </div>
            </div>

            {/* Modal */}
            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="md">
                <Modal.Header>
                    {currentItem
                        ? <>{isApproved ? 'Viewing (locked)' : (isEditing ? 'Editing' : 'Viewing')} "<span className="item-highlight">{currentItem.name}</span>"</>
                        : `Create ${title}`}
                </Modal.Header>
                <Modal.Body>
                    {isApproved && (
                        <div className="approved-banner">
                            <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
                        </div>
                    )}
                    <form data-testid="MOD_incident_master_form" id="master-form" onSubmit={(e) => handleSave('draft', e)} className="flex flex-col gap-y-4">
                        {/* System code — read-only, prefilled with the same code the list shows. */}
                        {currentItem && (
                            <FormControl.Input
                                label="Code"
                                value={(currentItem as any).code || '—'}
                                disabled
                                data-testid="INP_incident_master_code"
                            />
                        )}
                        <ValidatedInput
                            vh={fieldVh}
                            name="name"
                            code={109}
                            required
                            data-testid="INP_incident_master_name"
                            label="Name"
                            type="text"
                            value={formData.name}
                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                            placeholder="e.g., Operational Risk"
                            maxLength={100}
                            disabled={fieldsDisabled}
                        />
                        <ValidatedTextarea
                            vh={fieldVh}
                            name="description"
                            code={110}
                            data-testid="INP_incident_master_description"
                            label="Description"
                            value={formData.description}
                            onChange={e => setFormData({ ...formData, description: e.target.value })}
                            placeholder="Brief description..."
                            disabled={fieldsDisabled}
                        />
                        {masterKey === 'incidents' && (
                            <>
                                <FormControl.SearchSelect
                                    data-testid="SEL_incident_master_category"
                                    label="Category"
                                    required
                                    options={categoryOptions.map(c => ({ value: c.id, label: c.name }))}
                                    value={formData.incident_category_id ?? null}
                                    onChange={v => {
                                        setFormData({ ...formData, incident_category_id: v ? Number(v) : undefined });
                                        fieldVh.clearError('incident_category_id');
                                    }}
                                    placeholder={categoryOptions.length === 0 ? 'No event categories yet — create one first' : 'Select category…'}
                                    clearable
                                    disabled={fieldsDisabled}
                                    error={fieldVh.errors.incident_category_id}
                                    helper={categoryOptions.length === 0 ? 'Add an Event Category under Incident Reporting → Event Categories before creating an Incident Type.' : undefined}
                                />
                            </>
                        )}
                    </form>
                </Modal.Body>
                <Modal.Footer className={currentItem ? 'app-modal-footer--split' : ''}>
                    {/* Toggle Active/Inactive — shown alongside Edit (view) and Update (edit), but not if approved */}
                    {currentItem && !isApproved && (
                        <Button
                            data-testid="BTN_incident_master_toggle_active"
                            variant={currentItem.status === 'Active' ? 'inactive-outline' : 'active-outline'}
                            icon={currentItem.status === 'Active' ? CircleMinus : CircleCheck}
                            onClick={handleToggleActive}
                            disabled={toggling || submitting || permLoading || !canModify}
                            title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                        >
                            {toggling ? '...' : currentItem.status === 'Active' ? 'Inactive' : 'Active'}
                        </Button>
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                        {!currentItem && (
                            <WorkflowSaveBar
                                canDraft={canModify}
                                canSubmit={canModify}
                                canApprove={canApproveExtent}
                                busy={submitting}
                                onSave={(target) => handleSave(target)}
                            />
                        )}
                        {/* View mode → "Edit" enables the form (but not if approved). */}
                        {currentItem && !isEditing && !isApproved && (
                            <Button
                                data-testid="BTN_incident_master_edit"
                                variant="fill"
                                icon={Edit2}
                                onClick={() => {
                                    if (permLoading || !canModify) { setToast({ type: 'error', message: NOT_AUTHORISED_MSG }); return; }
                                    setIsEditing(true);
                                }}
                                disabled={permLoading || !canModify}
                                title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                            >
                                Edit
                            </Button>
                        )}
                        {/* Edit mode → "Update" saves (but not if approved). */}
                        {currentItem && isEditing && !isApproved && (
                            <Button
                                data-testid="BTN_incident_master_submit"
                                type="submit"
                                form="master-form"
                                variant="update"
                                icon={Check}
                                disabled={permLoading || !canModify || submitting || toggling}
                                title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                            >
                                {submitting ? 'Updating...' : 'Update'}
                            </Button>
                        )}
                    </div>
                </Modal.Footer>
            </Modal>

            <WorkflowModal
                isOpen={!!workflowRow}
                onClose={() => { setWorkflowRow(null); reload(); }}
                kind={WF_KIND[masterKey]}
                id={workflowRow?.id ?? null}
                onChanged={reload}
            />

            <Toast toast={toast} onClose={() => setToast(null)} />
        </>
    );
};

// --- Main ---
const IncidentMasters = () => {
    const [activeMaster, setActiveMaster] = useState<MasterKey>('incidents');
    const activeItem = MASTERS.find(m => m.key === activeMaster)!;

    return (
        <div className="flex h-full overflow-hidden">

            {/* Left nav card */}
            <div className="netbody-card side-nav-card">
                <div className="side-nav-card-heading">Configuration</div>
                <nav aria-label="Section navigation">
                    {MASTERS.map(({ key, label, icon: Icon }) => (
                        <button
                            data-testid={`NAV_incident_master_${key}`}
                            key={key}
                            onClick={() => setActiveMaster(key)}
                            className={`side-panel-menu-item${activeMaster === key ? ' side-panel-menu-item--active' : ''}`}
                        >
                            <Icon size={14} />
                            {label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Right card: toolbar + table */}
            <div className="netbody-card side-content-card flex flex-col flex-1 min-w-0">
                <MasterCRUD key={activeMaster} title={activeItem.label} masterKey={activeMaster} />
            </div>

        </div>
    );
};

export default IncidentMasters;
